import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { ButtonInternalLink } from "@src/appV2/redesign/components/ButtonInternalLink";
import { CollapsibleBlock, Text } from "@src/appV2/redesign/system/ui-components";
import { uniqueId } from "lodash";

import { ConsequenceText } from "../Activity/ConsequenceText";
import { ActivityItem } from "../Activity/Item";
import { CancellationCreditPenaltySubCard } from "./CancellationCreditPenaltySubCard";

export interface AdjustmentsSectionItem {
  title: string;
  consequence: number | "Instant restriction";
  multiplier?: number;
  hasCreditOption?: boolean;
  pointsWithCredit?: number;
  pointsWithoutCredit?: number;
  hasCredit?: boolean;
}

export interface AdjustmentsSectionProps {
  title: string;
  description?: string;
  callout?: React.ReactNode;
  items: AdjustmentsSectionItem[];
  expanded: boolean;
  onClick: () => void;
  hasContentContainer?: boolean;
  variant?: "pill" | "text";
}

export function AdjustmentsSection(props: AdjustmentsSectionProps) {
  const {
    title,
    description,
    items,
    callout,
    expanded,
    onClick,
    hasContentContainer = false,
    variant,
  } = props;

  const content = (
    <>
      {isDefined(description) && (
        <Stack direction="column" spacing={3} padding={3}>
          <Text variant="body2" color={(theme) => theme.palette.grey[600]}>
            {description}
          </Text>
        </Stack>
      )}
      <Stack direction="column" spacing={5}>
        {callout}
        {items.map((item) => {
          const {
            title,
            consequence,
            multiplier,
            hasCreditOption,
            pointsWithCredit,
            pointsWithoutCredit,
            hasCredit,
          } = item;

          if (hasCreditOption && isDefined(pointsWithCredit) && isDefined(pointsWithoutCredit)) {
            return (
              <CancellationCreditPenaltySubCard
                key={uniqueId("adjustment-section-credit-")}
                title={title}
                pointsWithCredit={pointsWithCredit}
                pointsWithoutCredit={pointsWithoutCredit}
                hasCredit={hasCredit ?? false}
              />
            );
          }

          return (
            <ActivityItem
              key={uniqueId("adjustment-section-")}
              label={title}
              consequence={consequence}
              multiplier={multiplier}
              consequenceElement={
                variant === "text" ? <ConsequenceText consequence={consequence} /> : undefined
              }
            />
          );
        })}
      </Stack>
    </>
  );

  return (
    <Stack paddingTop={6} spacing={6}>
      <CollapsibleBlock
        title={<Text variant="h5">{title}</Text>}
        expanded={expanded}
        LinkComponent={ButtonInternalLink}
        onChange={onClick}
      >
        {hasContentContainer ? (
          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.primary,
              borderRadius: "12px",
              padding: 4,
            })}
          >
            {content}
          </Box>
        ) : (
          content
        )}
      </CollapsibleBlock>
    </Stack>
  );
}
